import { useState, useEffect, useRef, useMemo } from 'react';
import { IVideoClip, VideoEditConfig } from '@packages/global';

export function usePlayback(
  videoClips: IVideoClip[],
  editConfig: VideoEditConfig | null
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevClipIdRef = useRef<string | null>(null); // 用于检测片段切换

  // 计算总时长
  const totalDuration = useMemo(() => {
    if (!editConfig) return 0;
    const clips = editConfig.clips || [];
    if (clips.length === 0) return 0;
    return Math.max(...clips.map(c => c.startTime + c.duration));
  }, [editConfig]);

  // 计算当前播放的 clip 和本地时间
  const currentPlaybackInfo = useMemo(() => {
    if (!editConfig) return null;

    const clips = editConfig.clips || [];

    for (let i = 0; i < clips.length; i++) {
      const clipInfo = clips[i];
      const clipEnd = clipInfo.startTime + clipInfo.duration;
      const isLastClip = i === clips.length - 1;

      // 最后一个 clip 用 <= 避免末尾帧落空；其他 clip 用 < 避免与下一个 clip 重叠
      const inRange = isLastClip
        ? currentTime >= clipInfo.startTime && currentTime <= clipEnd
        : currentTime >= clipInfo.startTime && currentTime < clipEnd;

      if (inRange) {
        const clip = videoClips.find(c => c.id === clipInfo.clipId);
        if (!clip) return null;

        const info = {
          clip,
          localTime: currentTime - clipInfo.startTime,
          trimStart: clipInfo.trimStart || 0,
          trimEnd: clipInfo.trimEnd || Infinity
        };

        // 更新当前片段引用
        if (prevClipIdRef.current !== clip.id) {
          prevClipIdRef.current = clip.id;
        }

        return info;
      }
    }

    return null;
  }, [currentTime, editConfig, videoClips]);

  // 播放控制 - 使用最简单的依赖
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 0.1;

          // 如果到达总时长，停止播放
          if (newTime >= totalDuration) {
            setIsPlaying(false);
            return totalDuration;
          }

          return newTime;
        });
      }, 100);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isPlaying, totalDuration]);

  // 清理
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    isPlaying,
    currentTime,
    currentPlaybackInfo,
    totalDuration,
    play: () => setIsPlaying(true),
    pause: () => {
      setIsPlaying(false);
      // 停止所有音频元素（包括 BGM）
      document.querySelectorAll('audio').forEach(audio => {
        audio.pause();
      });
    },
    seek: (time: number) => {
      setCurrentTime(Math.max(0, Math.min(totalDuration, time)));
    }
  };
}
